import AsyncStorage from '@react-native-async-storage/async-storage';
import NetInfo from '@react-native-community/netinfo';
import Constants from 'expo-constants';
import { getDevelopmentApiHeaders } from '../../../api/developmentHeaders';
import { getCompatibilityFeatureCategory, type CompatibilityDiagnostic } from '@hexia/survey-client/core';

type Platform = 'web' | 'ios' | 'android';

export type SurveyFallbackReport = {
  product: 'mystery-shopping';
  formVersionId: string;
  clientVersion: string;
  runtimeVersion: string;
  platform: Platform;
  diagnostics: readonly Pick<CompatibilityDiagnostic, 'code' | 'feature' | 'pointer'>[];
  /** Local-only deduplication key. This value is retained only on-device and is never included in the HTTP payload. */
  sessionKey?: string;
};

const queueKey = 'hexia:survey-compatibility-fallbacks';
const sentThisSession = new Set<string>();

const reportKey = (report: SurveyFallbackReport, diagnostic: SurveyFallbackReport['diagnostics'][number]) =>
  [report.sessionKey ?? 'app-session', report.formVersionId, report.runtimeVersion, report.platform, diagnostic.code, diagnostic.pointer ?? ''].join('|');

const sanitize = (report: SurveyFallbackReport): SurveyFallbackReport => ({
  product: 'mystery-shopping',
  formVersionId: report.formVersionId,
  clientVersion: report.clientVersion,
  runtimeVersion: report.runtimeVersion,
  platform: report.platform,
  diagnostics: report.diagnostics.map(({ code, pointer }) => ({
    code,
    feature: getCompatibilityFeatureCategory(code),
    pointer: pointer || undefined,
  })),
});

const readQueue = async (): Promise<SurveyFallbackReport[]> => {
  try {
    return JSON.parse(await AsyncStorage.getItem(queueKey) ?? '[]') as SurveyFallbackReport[];
  } catch {
    return [];
  }
};

const writeQueue = (queue: SurveyFallbackReport[]) => AsyncStorage.setItem(queueKey, JSON.stringify(queue));

const send = async (report: SurveyFallbackReport): Promise<void> => {
  const baseUrl = Constants.expoConfig?.extra?.baseUrl;
  const response = await fetch(`${baseUrl}/api/portal/survey-runtime/fallbacks`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json', ...getDevelopmentApiHeaders(baseUrl) },
    body: JSON.stringify(sanitize(report)),
  });

  if (!response.ok) {
    throw new Error(`Survey compatibility report failed with ${response.status}.`);
  }
};

export const flushSurveyFallbackReports = async (): Promise<void> => {
  const network = await NetInfo.fetch();
  if (!network.isConnected) return;

  const queue = await readQueue();
  const remaining: SurveyFallbackReport[] = [];

  for (const report of queue) {
    try {
      await send(report);
    } catch {
      remaining.push(report);
    }
  }

  await writeQueue(remaining);
};

export const reportSurveyFallback = async (report: SurveyFallbackReport): Promise<void> => {
  const diagnostics = report.diagnostics.filter((diagnostic) => {
    const key = reportKey(report, diagnostic);
    if (sentThisSession.has(key)) return false;
    sentThisSession.add(key);
    return true;
  });

  if (!diagnostics.length) return;
  const pending = { ...sanitize({ ...report, diagnostics }), sessionKey: report.sessionKey };

  try {
    await send(pending);
  } catch {
    const queue = await readQueue();
    const queuedKeys = new Set(queue.flatMap((item) => item.diagnostics.map((diagnostic) => reportKey(item, diagnostic))));
    const unsavedDiagnostics = diagnostics.filter((diagnostic) => !queuedKeys.has(reportKey(pending, diagnostic)));
    if (unsavedDiagnostics.length) await writeQueue([...queue, { ...pending, diagnostics: unsavedDiagnostics }]);
  }
};

let unsubscribe: (() => void) | undefined;

export const startSurveyFallbackRetry = (): (() => void) => {
  if (!unsubscribe) {
    unsubscribe = NetInfo.addEventListener((state) => {
      if (state.isConnected) void flushSurveyFallbackReports();
    });
  }

  void flushSurveyFallbackReports();
  return () => {
    unsubscribe?.();
    unsubscribe = undefined;
  };
};
